import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import LayoutWithSidebar from '../components/common/layoutWithSidebar';
import PhoneHeader, {
  PhoneNumbersEnvironment,
} from '../components/PhoneNumber/PhoneHeader';
import { useAlert } from '../hooks/useAlert';
import { FC, useEffect, useState } from 'react';
import { IAvailablePhoneNumbers } from '../schemas/apis/interface';
import {
  assignPhoneToAgentApi,
  getAvaiablePhoneNumbers,
  getSubaccountNumbersApi,
  removeAgentFromPhoneNumberApi,
} from '../utils/apis';
import {
  Box,
  Skeleton,
  Typography,
  Chip,
  Button,
  CircularProgress,
} from '@mui/material';
import { DataGrid, GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import theme from '../theme';
import AgentsDropDown from '../components/common/AgentsDropDown';
import MessagePopup from '../components/common/MessagePopup';
import NoDataFallback from '../components/common/NoDataFallback';
import { User, Phone } from '@phosphor-icons/react';
import { useUser } from '../hooks/useUser';
import {
  availablePhoneNumbersQueryConfig,
  availablePhoneNumbersQueryKey,
} from '../constants/availablePhoneNumbersQuery';

const tableContainerSx = {
  border: '1px solid rgba(224, 224, 224, 1)',
  borderRadius: '8px',
  overflow: 'hidden',
  position: 'relative',
  flex: 1,
  minHeight: 0,
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
  backgroundColor: 'white',
} as const;

function formatPhoneProviderLabel(raw?: string | null): string {
  const p = (raw ?? 'twilio').trim().toLowerCase();
  if (p === 'twilio') return 'Twilio';
  if (p === 'plivo') return 'Plivo';
  const t = raw?.trim();
  return t ? t.charAt(0).toUpperCase() + t.slice(1).toLowerCase() : 'Twilio';
}

const productionPanelOuterSx = {
  flex: 1,
  minHeight: 0,
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
} as const;

const PhoneNumbers = () => {
  const [selectedEnvironment, setSelectedEnvironment] =
    useState<PhoneNumbersEnvironment>('trial');

  return (
    <LayoutWithSidebar margin={2} disableMainScroll>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          overflow: 'hidden',
          pb: 3,
        }}
      >
        <Box sx={{ flexShrink: 0 }}>
          <PhoneHeader
            selectedEnvironment={selectedEnvironment}
            onEnvironmentChange={setSelectedEnvironment}
          />
        </Box>
        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            mt: 1,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          }}
        >
          <PhoneNumberCards environment={selectedEnvironment} />
        </Box>
      </Box>
    </LayoutWithSidebar>
  );
};

export default PhoneNumbers;

interface Agent {
  agentName: string;
  _id: string;
}

export const PhoneNumberCards: FC<{
  environment?: PhoneNumbersEnvironment;
}> = ({ environment = 'trial' }) => {
  const queryClient = useQueryClient();
  const { alert, showAlert, setAlert } = useAlert();
  const { user, isLoading: isUserLoading } = useUser();
  const [selectedAgent, setSelectedAgent] = useState<Agent | null>(null);
  const [phoneNumbersList, setPhoneNumbersList] =
    useState<IAvailablePhoneNumbers>();
  const [assigningPhoneNumber, setAssigningPhoneNumber] = useState<
    string | null
  >(null);
  const [unassigningPhoneNumber, setUnassigningPhoneNumber] = useState<
    string | null
  >(null);

  const isProductionEnvironment = environment === 'production';
  const phoneNumbersQueryKey = isProductionEnvironment
    ? (['available-phone-numbers', 'prod'] as const)
    : availablePhoneNumbersQueryKey;

  const { data: phoneNumbers, isLoading } = useQuery({
    queryKey: phoneNumbersQueryKey,
    queryFn: () =>
      isProductionEnvironment
        ? getSubaccountNumbersApi()
        : getAvaiablePhoneNumbers(),
    ...availablePhoneNumbersQueryConfig,
    enabled:
      !isProductionEnvironment ||
      (isProductionEnvironment && user?.accountType === 'prod'),
  });

  const { mutateAsync: assignNumber } = useMutation({
    mutationFn: assignPhoneToAgentApi,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['agents'] });
      queryClient.invalidateQueries({
        queryKey: phoneNumbersQueryKey,
      });
      showAlert(
        `Number assigned successfully to ${selectedAgent?.agentName}`,
        'success'
      );
      setAssigningPhoneNumber(null);
    },
    onError: (error: any) => {
      showAlert(
        error?.response?.data?.message || 'Something went wrong',
        'error'
      );
      setAssigningPhoneNumber(null);
    },
  });

  const {
    mutateAsync: removeAgentFromPhoneNumber,
    isPending: isUnassigningNumber,
  } = useMutation({
    mutationFn: removeAgentFromPhoneNumberApi,
    onSuccess: async () => {
      await queryClient.refetchQueries({
        queryKey: phoneNumbersQueryKey,
        exact: true,
      });
      showAlert('Agent removed from phone number', 'success');
      setUnassigningPhoneNumber(null);
    },
    onError: (error: any) => {
      showAlert(
        error?.response?.data?.message || 'Something went wrong',
        'error'
      );
      setUnassigningPhoneNumber(null);
    },
  });

  const handleAgentSelect = async (agent: Agent, phoneNumber: string) => {
    setSelectedAgent(agent);
    setAssigningPhoneNumber(phoneNumber);
    try {
      await assignNumber({
        agentId: agent._id,
        phoneNumber: phoneNumber,
      });
    } catch (error) {
      console.error('Error assigning number:', error);
      setAssigningPhoneNumber(null);
    }
  };

  const handleUnassignAgent = async (
    phoneNumber: string,
    numberType?: 'global' | 'subaccount' | null
  ) => {
    // Backend expects `type: trial | prod` based on the phone account scope.
    const requestType: 'trial' | 'prod' =
      numberType === 'subaccount' ? 'prod' : 'trial';
    setUnassigningPhoneNumber(phoneNumber);
    try {
      await removeAgentFromPhoneNumber({
        phoneNumber: phoneNumber,
        type: requestType,
      });
    } catch (error) {
      console.error('Error unassigning agent:', error);
    }
  };

  useEffect(() => {
    if (phoneNumbers) {
      const modifiedData = {
        ...phoneNumbers.data,
        activeNumbers: [...(phoneNumbers.data?.activeNumbers || [])],
      };

      setPhoneNumbersList(modifiedData);
    }
  }, [phoneNumbers]);

  const trialPhoneNumbers = phoneNumbersList?.activeNumbers ?? [];

  const rows =
    trialPhoneNumbers.map((phoneNumber, index) => {
      return {
        id: `${phoneNumber.phoneNumber}-${index}`,
        phoneNumber: phoneNumber.phoneNumber,
        providerLabel: formatPhoneProviderLabel(phoneNumber.provider),
        region: phoneNumber.region || null,
        userName: phoneNumber.userName || null,
        agentName: phoneNumber.agentName || null,
        type: 'Inbound',
        status: phoneNumber.status === 'assigned' ? 'Active' : 'Inactive',
        originalData: phoneNumber,
      };
    }) || [];

  const columns: GridColDef[] = [
    {
      field: 'phoneNumber',
      headerName: 'Phone Number',
      flex: 2,
      headerClassName: 'data-grid-header',
      renderCell: (params: GridRenderCellParams) => (
        <Box
          sx={{
            display: 'flex',
            gap: 1.5,
            alignItems: 'center',
          }}
        >
          <Box
            sx={{
              width: 32,
              height: 32,
              borderRadius: '50%',
              bgcolor: '#F3F4F6',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Phone size={16} color="#6B7280" />
          </Box>
          <Typography
            sx={{
              color: theme.palette.custom.textBlue,
              fontSize: '14px',
              fontWeight: 500,
              lineHeight: 1.2,
            }}
          >
            {params.value}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'providerLabel',
      headerName: 'Provider',
      flex: 1,
      headerClassName: 'data-grid-header',
      renderCell: (params: GridRenderCellParams) => (
        <Typography
          sx={{
            color: theme.palette.lightGray.text,
            fontSize: '14px',
            fontWeight: 400,
          }}
        >
          {params.value || 'N/A'}
        </Typography>
      ),
    },
    {
      field: 'region',
      headerName: 'Region',
      flex: 1.5,
      headerClassName: 'data-grid-header',
      renderCell: (params: GridRenderCellParams) => (
        <Typography
          sx={{
            color: theme.palette.lightGray.text,
            fontSize: '14px',
            fontWeight: 400,
          }}
        >
          {params.value || 'N/A'}
        </Typography>
      ),
    },
    {
      field: 'assignedTo',
      headerName: 'Assigned To',
      flex: 2.5,
      headerClassName: 'data-grid-header',
      renderCell: (params: GridRenderCellParams) => (
        <Box
          sx={{
            display: 'flex',
            gap: 1.5,
            alignItems: 'center',
          }}
        >
          {params.row.userName || params.row.agentName ? (
            <>
              <Box
                sx={{
                  width: 32,
                  height: 32,
                  borderRadius: '50%',
                  bgcolor: '#F3F4F6',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <User size={16} color="#6B7280" />
              </Box>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                {params.row.userName && (
                  <Typography
                    sx={{
                      color: theme.palette.custom.textBlue,
                      fontSize: '14px',
                      fontWeight: 500,
                      lineHeight: 1.2,
                    }}
                  >
                    {params.row.userName}
                  </Typography>
                )}
                {params.row.agentName && (
                  <Typography
                    sx={{
                      color: theme.palette.lightGray.text,
                      fontSize: '12px',
                      fontWeight: 400,
                      lineHeight: 1.2,
                    }}
                  >
                    {params.row.agentName}
                  </Typography>
                )}
              </Box>
            </>
          ) : (
            <Typography
              sx={{
                color: theme.palette.lightGray.text,
                fontSize: '14px',
                fontWeight: 400,
              }}
            >
              Not assigned
            </Typography>
          )}
        </Box>
      ),
    },
    {
      field: 'status',
      headerName: 'Status',
      flex: 1,
      headerClassName: 'data-grid-header',
      renderCell: (params: GridRenderCellParams) => (
        <Chip
          label={params.value}
          size="small"
          icon={
            <Box
              sx={{
                width: 6,
                height: 6,
                borderRadius: '50%',
                bgcolor: params.value === 'Active' ? '#10B981' : '#6B7280',
              }}
            />
          }
          sx={{
            backgroundColor: params.value === 'Active' ? '#D1FAE5' : '#F3F4F6',
            color: params.value === 'Active' ? '#10B981' : '#6B7280',
            border: 'none',
            fontSize: '12px',
            fontWeight: 500,
            '& .MuiChip-label': {
              px: 1,
            },
            '& .MuiChip-icon': {
              ml: 1,
              mr: 0.5,
            },
          }}
        />
      ),
    },
    {
      field: 'actions',
      headerName: 'Action',
      flex: 1.5,
      headerClassName: 'data-grid-header',
      headerAlign: 'left',
      align: 'left',
      renderCell: (params: GridRenderCellParams) => {
        const isRowUnassigning =
          isUnassigningNumber &&
          unassigningPhoneNumber === params.row.phoneNumber;
        return (
          <Box
            sx={{
              display: 'flex',
              gap: 1,
              alignItems: 'center',
              width: '100%',
            }}
          >
            {!(params.row.userName || params.row.agentName) ? (
              <AgentsDropDown
                onAgentSelect={agent =>
                  void handleAgentSelect(agent, params.row.phoneNumber)
                }
                isPhoneNumberPage={true}
                isPending={assigningPhoneNumber === params.row.phoneNumber}
                phoneAccountFilter={isProductionEnvironment ? 'prod' : 'trial'}
              />
            ) : (
              <>
                {user?.tenantName === params.row.originalData.tenantName ? (
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() =>
                      handleUnassignAgent(
                        params.row.phoneNumber,
                        params.row.originalData.numberType
                      )
                    }
                    disabled={isRowUnassigning}
                    sx={{
                      minWidth: 'auto',
                      px: 1.5,
                      py: 0.5,
                      fontSize: '12px',
                      borderColor: '#DC2626',
                      color: '#DC2626',
                      '&:hover': {
                        borderColor: '#B91C1C',
                        backgroundColor: '#FEF2F2',
                      },
                    }}
                  >
                    {isRowUnassigning ? (
                      <CircularProgress size={16} />
                    ) : (
                      'Unassign'
                    )}
                  </Button>
                ) : (
                  <Chip
                    label="Already Assigned"
                    size="small"
                    sx={{
                      backgroundColor: '#F3F4F6',
                      color: '#6B7280',
                      fontSize: '12px',
                      fontWeight: 500,
                    }}
                  />
                )}
              </>
            )}
          </Box>
        );
      },
    },
  ];

  const gridTemplateColumns = '2fr 1fr 1.5fr 2.5fr 1fr 1.5fr';

  const LoadingSkeleton = () => (
    <Box sx={{ width: '100%' }}>
      {[...Array(10)].map((_, index) => (
        <Box
          key={index}
          sx={{
            display: 'grid',
            gridTemplateColumns,
            gap: 2,
            p: 2,
            borderBottom: '1px solid rgba(224, 224, 224, 1)',
            alignItems: 'center',
          }}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            <Skeleton variant="text" width="80%" height={20} />
          </Box>
          <Skeleton variant="text" width="50%" height={16} />
          <Skeleton variant="text" width="60%" height={16} />
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Skeleton
              variant="rectangular"
              width={80}
              height={24}
              sx={{ borderRadius: '6px' }}
            />
            <Skeleton
              variant="rectangular"
              width={80}
              height={24}
              sx={{ borderRadius: '6px' }}
            />
          </Box>
          <Skeleton
            variant="rectangular"
            width={60}
            height={24}
            sx={{ borderRadius: '12px' }}
          />
          <Skeleton variant="rectangular" width="80%" height={32} />
        </Box>
      ))}
    </Box>
  );

  const cardsRootSx = {
    flex: 1,
    minHeight: 0,
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  } as const;

  if (isProductionEnvironment && isUserLoading) {
    return (
      <Box sx={productionPanelOuterSx}>
        <Box
          sx={{
            ...tableContainerSx,
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <CircularProgress size={32} />
        </Box>
      </Box>
    );
  }

  if (isProductionEnvironment && user?.accountType !== 'prod') {
    return (
      <Box sx={productionPanelOuterSx}>
        <Box
          sx={{
            ...tableContainerSx,
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <NoDataFallback
            message="Production numbers are not available on a trial account"
            description="Upgrade your account to production type to view and manage production phone numbers."
          />
        </Box>
      </Box>
    );
  }

  if (trialPhoneNumbers.length === 0 && !isLoading) {
    return (
      <Box sx={cardsRootSx}>
        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <NoDataFallback
            message={
              isProductionEnvironment
                ? 'No production phone numbers available'
                : 'No trial phone numbers available'
            }
            description={
              isProductionEnvironment
                ? 'There are no production phone numbers available right now.'
                : 'There are no trial phone numbers available right now.'
            }
          />
        </Box>
        <MessagePopup alert={alert} setAlert={setAlert} />
      </Box>
    );
  }

  return (
    <Box sx={cardsRootSx}>
      <Box sx={tableContainerSx}>
        {isLoading ? (
          <>
            <Box
              sx={{
                flexShrink: 0,
                bgcolor: '#F8F9FC',
                display: 'grid',
                gridTemplateColumns,
                gap: 2,
                p: 2,
                borderBottom: '1px solid rgba(224, 224, 224, 1)',
              }}
            >
              {(
                [
                  'Phone Number',
                  'Provider',
                  'Region',
                  'Assigned To',
                  'Status',
                  'Action',
                ] as const
              ).map(label => (
                <Typography
                  key={label}
                  sx={{
                    color: 'custom.textBlue',
                    fontSize: '14px',
                    fontWeight: 600,
                  }}
                >
                  {label}
                </Typography>
              ))}
            </Box>
            <Box
              sx={{
                flex: 1,
                minHeight: 0,
                overflow: 'auto',
              }}
            >
              <LoadingSkeleton />
            </Box>
          </>
        ) : (
          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              width: '100%',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <DataGrid
              rows={rows}
              columns={columns}
              loading={false}
              disableRowSelectionOnClick
              initialState={{
                pagination: {
                  paginationModel: { page: 0, pageSize: 100 },
                },
              }}
              pageSizeOptions={[10, 20, 50, 75, 100]}
              sx={{
                flex: 1,
                minHeight: 0,
                height: '100%',
                '& .data-grid-header': {
                  bgcolor: '#F8F9FC',
                  color: 'custom.textBlue',
                  fontSize: '14px',
                  fontWeight: 600,
                  padding: '18px 16px',
                },
                '& .MuiDataGrid-cell': {
                  padding: '18px 16px',
                  fontSize: '14px',
                  display: 'flex',
                  alignItems: 'center',
                  '&:focus, &:focus-within': {
                    outline: 'none !important',
                  },
                },
                border: 'none',
                '& .MuiDataGrid-cell:focus-within': {
                  outline: 'none !important',
                },
              }}
              slots={{
                noRowsOverlay: () => (
                  <Box sx={{ p: 2, textAlign: 'center' }}>
                    No phone numbers available
                  </Box>
                ),
              }}
            />
          </Box>
        )}
      </Box>
      <MessagePopup alert={alert} setAlert={setAlert} />
    </Box>
  );
};
